<template>
    <div
        v-if="item.layout"
        class="mx-1 py-1"
    >
        <div
            :style="getCardStyles"
            class="main-bg-dark px-4 py-4 flex items-center justify-between rounded-lg shadow"
        >
            <div class="flex flex-col w-full items-center justify-between">
                <slot name="label">
                    <div class="flex">
                        <h5
                            v-if="item.layout.showText"
                            :style="getTextFontSize"
                            class="font-bold mx-1"
                        >
                            {{ itemLabel }}
                        </h5>
                    </div>
                </slot>
                <slot name="value">
                    <div class="flex items-center">
                        <span
                            v-if="item.showAbsoluteNumbers"
                            class="text-xl px-1"
                        >({{ item.count }})</span>
                        <h2
                            :style="getValueFontSize"
                            class="font-bold -my-2"
                        >
                            {{ formatItemValue }}
                        </h2>
                    </div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import i18n from '@/i18n'
import get from 'lodash/get'
import { timeFormatter } from '@/helpers/timeFormatter'
import { AVG_RING_TIME_KEY, MAX_RING_TIME_KEY } from '@/enum/redesignQueueDashboardStatistics'
import { billingCdrQueueTypes } from '@/helpers/multiQueueDashboardRedesign'
import { WidgetExtended } from '@/types/dashboard'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'

/* Props */
const props = withDefaults(
    defineProps<{
        item: object
        widget: WidgetExtended
        isPercentage?: boolean
    }>(),
    {
        isPercentage: false
    }
)

/* Composable */
const { getColumnTagsByProp } = useTableHeaderTranslations()

/* Computed */
const itemLabel = computed(() => {
    let finalTranslationKey = props.item.label

    const tableTranslationProp = billingCdrQueueTypes[props.item.key] ?? props.item.key

    const { translateOfHeaderTitleFull } = getColumnTagsByProp(tableTranslationProp)

    if (i18n.te(translateOfHeaderTitleFull)) {
        finalTranslationKey = translateOfHeaderTitleFull
    }

    const finalTranslation = i18n.t(finalTranslationKey)

    if (props.isPercentage) {
        return finalTranslation + ' %'
    }

    return finalTranslation
})
const getTextFontSize = computed(() => {
    const fontSize = get(props.widget, 'WidgetLayout.titleFontSize', '24')
    return {
        'font-size': `${fontSize}px`
    }
})
const getValueFontSize = computed(() => {
    const fontSize = get(props.widget, 'WidgetLayout.valueFontSize', '36')
    return {
        'font-size': `${fontSize}px`
    }
})
const getCardStyles = computed(() => {
    const item = props.item
    const color = props.widget.WidgetLayout.colors && item.columnKey in props.widget.WidgetLayout.colors && props.widget.WidgetLayout.colors[item.columnKey] ? props.widget.WidgetLayout.colors[item.columnKey] : item.colors.fonts

    let styles = {
        color: color,
        'min-width': `${item.layout.minWidth}px`,
        'max-width': `${item.layout.maxWidth}px`
    }

    if (item.layout.showBorder) {
        styles = {
            ...styles,
            ...{
                border: `2px solid ${color}`
            }
        }
    }
    return styles
})
const formatItemValue = computed(() => {
    if ([ MAX_RING_TIME_KEY, AVG_RING_TIME_KEY ].includes(props.item.key)) {
        return timeFormatter(props.item.value)
    }
    return props.item.value
})
</script>
